import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useUser } from '../../contexts/UserContext'
import { StakeholderType, UserType } from '../../types/user'
import NotificationBadge from '../NotificationBadge'
import ProfileSwitcher from '../ProfileSwitcher'

// Interface for navigation items
interface NavItem {
  id: string
  icon: React.ReactNode
  url: string
  label: React.ReactNode | string
  renderExtra?: () => React.ReactNode
}

const Sidebar: React.FC = () => {
  const [tagsOpen, setTagsOpen] = useState<boolean>(false)
  const [projectsOpen, setProjectsOpen] = useState<boolean>(false)
  const [workOrdersOpen, setWorkOrdersOpen] = useState<boolean>(false)
  const [invoicesOpen, setInvoicesOpen] = useState<boolean>(false)
  const [statusReportsOpen, setStatusReportsOpen] = useState<boolean>(false)
  const { user } = useUser()

  const location = useLocation()
  const currentProfile = user?.current_profile?.stakeholder_type
  const isDriver = user?.user_type === 'DRIVER' // ✅ NEW: Check user_type instead of profile
  const isOnlyDriver =
    user?.user_type === 'DRIVER' &&
    !user?.stakeholder_types?.some((type) =>
      ['BROKER', 'CONTRACTOR', 'SUB_HAULER'].includes(type),
    )
  const isBroker = currentProfile === StakeholderType.BROKER
  const isContractor = currentProfile === StakeholderType.CONTRACTOR
  const isSubhauler = currentProfile === StakeholderType.SUB_HAULER
  const isSiteSupervisor = user?.user_type === UserType.SITE_SUPERVISOR
  const isDispatcher = user?.user_type === UserType.DISPATCHER
  const canViewDispatches = isBroker || isSubhauler || isContractor

  // Use the centralized notification context
  const { tags, projects, workOrders, invoices } = useNotificationContext()

  useEffect(() => {
    if (location.pathname.includes('tags')) {
      setTagsOpen(true)
    }
    if (location.pathname.includes('projects')) {
      setProjectsOpen(true)
    }
    if (location.pathname.includes('work-orders')) {
      setWorkOrdersOpen(true)
    }
    if (location.pathname.includes('invoices')) {
      setInvoicesOpen(true)
    }
    if (location.pathname.includes('status-reports')) {
      setStatusReportsOpen(true)
    }
  }, [location])

  // Mark accepted projects as read when navigating to accepted tab
  useEffect(() => {
    if (
      location.pathname === '/projects/accepted' &&
      (isBroker || isContractor || isSubhauler)
    ) {
      projects.markAcceptedAsRead()
    }
  }, [
    location.pathname,
    isBroker,
    isContractor,
    isSubhauler,
    projects.markAcceptedAsRead,
  ])

  // Mark accepted work orders as read when navigating to accepted tab
  useEffect(() => {
    if (location.pathname === '/work-orders/accepted' && canViewDispatches) {
      workOrders.markAllAcceptedAsRead()
    }
  }, [location.pathname, canViewDispatches, workOrders.markAllAcceptedAsRead])

  // Mark pending work orders as read when navigating to pending tab
  useEffect(() => {
    if (location.pathname === '/work-orders/pending' && canViewDispatches) {
      workOrders.markAllPendingAsRead()
    }
  }, [location.pathname, canViewDispatches, workOrders.markAllPendingAsRead])

  // Navigation item for projects - not shown to drivers
  const projectsItem: NavItem = {
    id: 'projects',
    icon: <i className={`fa-solid fa-folder ${css(styles.icon)}`} />,
    url: '/projects/drafts',
    renderExtra: () => {
      return (
        projectsOpen && (
          <div className={css(styles.subNav)}>
            <Link
              to="/projects/drafts"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/projects/drafts' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>Draft</div>
              </div>
            </Link>
            <Link
              to="/projects/pending"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/projects/pending' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>
                  Pending
                  {(isBroker || isContractor || isSubhauler) && (
                    <NotificationBadge count={projects.unreadCount} size="sm" />
                  )}
                </div>
              </div>
            </Link>
            <Link
              to="/projects/accepted"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/projects/accepted' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>Accepted</div>
              </div>
            </Link>
            <Link
              to="/projects/declined"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/projects/declined' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>Declined</div>
              </div>
            </Link>
          </div>
        )
      )
    },
    label: (
      <div className={css(styles.tagLabel)}>
        <Link to="/projects/drafts" className={css(styles.link)}>
          Project Terms
        </Link>
        {(isBroker || isContractor || isSubhauler) && (
          <NotificationBadge count={projects.unreadCount} size="md" />
        )}
        <button
          className={css(styles.tagIconContainer)}
          onClick={(e) => {
            e.preventDefault()
            setProjectsOpen(!projectsOpen)
            e.stopPropagation()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setProjectsOpen(!projectsOpen)
              e.stopPropagation()
            }
          }}
        >
          {projectsOpen ? (
            <i className="fa-solid fa-chevron-up" />
          ) : (
            <i className="fa-solid fa-chevron-down" />
          )}
        </button>
      </div>
    ),
  }

  // Navigation item for work orders (Dispatches) - shown to brokers and subhaulers
  const workOrdersItem: NavItem = {
    id: 'workorders',
    icon: <i className={`fa-solid fa-file-lines ${css(styles.icon)}`} />,
    url: '/work-orders/drafts',
    renderExtra: () => {
      return (
        workOrdersOpen && (
          <div className={css(styles.subNav)}>
            <Link
              to="/work-orders/drafts"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/work-orders/drafts' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>Draft</div>
              </div>
            </Link>
            <Link
              to="/work-orders/pending"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/work-orders/pending' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>
                  Pending
                  {canViewDispatches && (
                    <NotificationBadge
                      count={workOrders.pendingCount}
                      size="sm"
                    />
                  )}
                </div>
              </div>
            </Link>
            <Link
              to="/work-orders/accepted"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/work-orders/accepted' &&
                    styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>
                  Accepted
                  {canViewDispatches && (
                    <NotificationBadge
                      count={workOrders.acceptedCount}
                      size="sm"
                    />
                  )}
                </div>
              </div>
            </Link>
            <Link
              to="/work-orders/completed"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/work-orders/completed' &&
                    styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>Completed</div>
              </div>
            </Link>
            <Link
              to="/work-orders/declined"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/work-orders/declined' &&
                    styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>Declined</div>
              </div>
            </Link>
          </div>
        )
      )
    },
    label: (
      <div className={css(styles.tagLabel)}>
        <Link to="/work-orders/drafts" className={css(styles.link)}>
          Dispatches
        </Link>
        {canViewDispatches && (
          <NotificationBadge count={workOrders.unreadCount} size="md" />
        )}
        <button
          className={css(styles.tagIconContainer)}
          onClick={(e) => {
            e.preventDefault()
            setWorkOrdersOpen(!workOrdersOpen)
            e.stopPropagation()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setWorkOrdersOpen(!workOrdersOpen)
              e.stopPropagation()
            }
          }}
        >
          {workOrdersOpen ? (
            <i className="fa-solid fa-chevron-up" />
          ) : (
            <i className="fa-solid fa-chevron-down" />
          )}
        </button>
      </div>
    ),
  }

  // Navigation item for tags - shown to all users
  const tagsItem: NavItem = {
    id: 'tags',
    url: '/tags/drafts',
    icon: <i className={`fa-solid fa-tags ${css(styles.icon)}`} />,
    renderExtra: () => {
      return (
        tagsOpen && (
          <div className={css(styles.subNav)}>
            {/* Drafts tab - only shown to non-drivers */}
            {!isDriver && (
              <Link
                to="/tags/drafts"
                onClick={(e) => e.stopPropagation()}
                className={css(styles.link, styles.fullWidthLink)}
              >
                <div
                  className={css(
                    styles.nav,
                    styles.innerNav,
                    location.pathname === '/tags/drafts' && styles.active,
                  )}
                >
                  <div className={css(styles.subNavLabel)}>Drafts</div>
                </div>
              </Link>
            )}
            <Link
              to="/tags/open"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/tags/open' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>
                  Open Tags
                  {isDriver && (
                    <NotificationBadge count={tags.unreadCount} size="sm" />
                  )}
                </div>
              </div>
            </Link>
            <Link
              to="/tags/completed"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/tags/completed' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>Completed Tags</div>
              </div>
            </Link>
          </div>
        )
      )
    },
    label: (
      <div className={css(styles.tagLabel)}>
        <Link
          to={isDriver ? '/tags/open' : '/tags/drafts'}
          className={css(styles.link)}
        >
          Tags
        </Link>
        {isDriver && <NotificationBadge count={tags.unreadCount} size="md" />}
        <button
          className={css(styles.tagIconContainer)}
          onClick={(e) => {
            e.preventDefault()
            setTagsOpen(!tagsOpen)
            e.stopPropagation()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setTagsOpen(!tagsOpen)
              e.stopPropagation()
            }
          }}
        >
          {tagsOpen ? (
            <i className="fa-solid fa-chevron-up" />
          ) : (
            <i className="fa-solid fa-chevron-down" />
          )}
        </button>
      </div>
    ),
  }

  // Navigation item for contacts
  const contactsItem: NavItem = {
    id: 'contacts',
    icon: <i className={`fa-solid fa-address-book ${css(styles.icon)}`} />,
    url: '/contacts',
    label: 'Directory & Invites',
  }

  // Navigation item for invoices - shown to all users
  const invoicesItem: NavItem = {
    id: 'invoices',
    icon: <i className={`fa-solid fa-file-invoice ${css(styles.icon)}`} />,
    url: '/invoices/sent',
    renderExtra: () => {
      return (
        invoicesOpen && (
          <div className={css(styles.subNav)}>
            <Link
              to="/invoices/sent"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/invoices/sent' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>
                  Accounts Receivable
                </div>
              </div>
            </Link>
            <Link
              to="/invoices/received"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/invoices/received' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>
                  Accounts Payable
                  <NotificationBadge count={invoices.unreadCount} size="sm" />
                </div>
              </div>
            </Link>
          </div>
        )
      )
    },
    label: (
      <div className={css(styles.tagLabel)}>
        <Link to="/invoices/sent" className={css(styles.link)}>
          Invoices
        </Link>
        <button
          className={css(styles.tagIconContainer)}
          onClick={(e) => {
            e.preventDefault()
            setInvoicesOpen(!invoicesOpen)
            e.stopPropagation()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setInvoicesOpen(!invoicesOpen)
              e.stopPropagation()
            }
          }}
        >
          {invoicesOpen ? (
            <i className="fa-solid fa-chevron-up" />
          ) : (
            <i className="fa-solid fa-chevron-down" />
          )}
        </button>
      </div>
    ),
  }

  // Navigation item for status reports - shown to all users
  const statusReportsItem: NavItem = {
    id: 'status-reports',
    icon: <i className={`fa-solid fa-chart-line ${css(styles.icon)}`} />,
    url: '/status-reports/sent',
    renderExtra: () => {
      return (
        statusReportsOpen && (
          <div className={css(styles.subNav)}>
            <Link
              to="/status-reports/sent"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/status-reports/sent' && styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>Sent</div>
              </div>
            </Link>
            <Link
              to="/status-reports/received"
              onClick={(e) => e.stopPropagation()}
              className={css(styles.link, styles.fullWidthLink)}
            >
              <div
                className={css(
                  styles.nav,
                  styles.innerNav,
                  location.pathname === '/status-reports/received' &&
                    styles.active,
                )}
              >
                <div className={css(styles.subNavLabel)}>Received</div>
              </div>
            </Link>
          </div>
        )
      )
    },
    label: (
      <div className={css(styles.tagLabel)}>
        <Link to="/status-reports/sent" className={css(styles.link)}>
          Status Reports
        </Link>
        <button
          className={css(styles.tagIconContainer)}
          onClick={(e) => {
            e.preventDefault()
            setStatusReportsOpen(!statusReportsOpen)
            e.stopPropagation()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setStatusReportsOpen(!statusReportsOpen)
              e.stopPropagation()
            }
          }}
        >
          {statusReportsOpen ? (
            <i className="fa-solid fa-chevron-up" />
          ) : (
            <i className="fa-solid fa-chevron-down" />
          )}
        </button>
      </div>
    ),
  }

  // Navigation item for drivers & trucks - shown to all non-driver users
  const trucksItem: NavItem = {
    id: 'trucks',
    icon: <i className={`fa-solid fa-truck ${css(styles.icon)}`} />,
    url: '/trucks',
    label: 'Drivers & Trucks',
  }

  // Navigation item for settings - shown to all users
  const settingsItem: NavItem = {
    id: 'settings',
    icon: <i className={`fa-solid fa-cog ${css(styles.icon)}`} />,
    url: '/profile',
    label: 'Settings',
  }

  // Build the navigation menu based on current profile
  const navItems: NavItem[] = []

  // Add projects if user is not a driver (BROKER, CONTRACTOR, and SUB_HAULER can all create project terms)
  if (!isDriver) {
    navItems.push(projectsItem)
  }

  // Add work orders if current profile is BROKER, CONTRACTOR, or SUB_HAULER and user is not a driver
  if (!isDriver && canViewDispatches) {
    navItems.push(workOrdersItem)
  }

  // Add tags for all users (previously only shown to drivers)
  navItems.push(tagsItem)

  // Add contacts to navItems for all users
  navItems.push(contactsItem)

  // Add trucks for non-drivers only (drivers don't manage trucks directly)
  if (!isDriver) {
    navItems.push(trucksItem)
  }

  // Add invoices only for non-drivers, non-site-supervisors, and non-dispatchers
  if (!isDriver && !isSiteSupervisor && !isDispatcher) {
    navItems.push(invoicesItem)
  }

  // Add status reports only for non-drivers, non-site-supervisors, and non-dispatchers
  if (!isDriver && !isSiteSupervisor && !isDispatcher) {
    navItems.push(statusReportsItem)
  }

  // Add settings for all users
  navItems.push(settingsItem)

  return (
    <div className={css(styles.sidebar)}>
      <div className={css(styles.navItems)}>
        {navItems.map((navItem) => {
          return (
            <div
              key={navItem.url}
              className={css(
                styles.nav,
                location.pathname === navItem.url &&
                  !location.pathname.includes('/tags') &&
                  !location.pathname.includes('/projects') &&
                  !location.pathname.includes('/work-orders') &&
                  !location.pathname.includes('/invoices') &&
                  !location.pathname.includes('/status-reports') &&
                  styles.active,
              )}
            >
              <Link
                to={navItem.url}
                className={css(styles.link)}
                key={navItem.id}
              >
                <div className={css(styles.label)}>
                  <span className={css(styles.iconContainer)}>
                    {navItem.icon}
                  </span>{' '}
                  {navItem.label}
                </div>
              </Link>
              {navItem?.renderExtra?.()}
            </div>
          )
        })}
      </div>
      <ProfileSwitcher />
    </div>
  )
}

export default Sidebar

const styles = StyleSheet.create({
  sidebar: {
    width: 250,
    backgroundColor: '#1F2A37',
    minHeight: 'calc(100vh - 64px)', // Subtract Topbar height
    position: 'fixed',
    top: 64, // Start below Topbar
    left: 0,
    zIndex: 999,
    '@media only screen and (max-width: 1023px)': {
      display: 'none',
    },
    display: 'flex',
    flexDirection: 'column',
  },
  navItems: {
    flex: 1,
    marginLeft: 8,
  },
  iconContainer: {},
  icon: {
    opacity: 0.7,
  },
  nav: {
    padding: 8,
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 500,
  },
  label: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr',
    alignItems: 'center',
    gap: 8,
  },
  link: {
    color: '#fff',
    textDecoration: 'none',
  },
  fullWidthLink: {
    display: 'block',
    width: '100%',
  },
  subNav: {
    paddingTop: 8,
  },
  innerNav: {
    paddingLeft: 36,
  },
  active: {
    background: '#374151',
    borderRadius: 8,
  },
  tagLabel: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  tagIconContainer: {
    marginLeft: 'auto',
    padding: 8,
    ':hover': {
      background: '#374151',
      borderRadius: 8,
    },
  },
  subNavLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
})
